import Image from 'next/image';
import Skeleton from 'react-loading-skeleton';
import {
  MecProduct,
  isPrimaryProductImage,
} from '@adapters/bigcommerce/types/product';
import { useLocale } from '@hooks/useLocale';
import { useProductPrice } from '@hooks/useProductPrice';
import useFormattedPrice from '@hooks/useFormattedPrice';
import { normalizeImageVariantPreference } from '@utils/imageVariantPreference';

import styles from './CompareBarCard.module.scss';

const THUMBNAIL_SIZE = 60;

export interface CompareBarCardProps {
  /** Omitted while the product is still pending, which renders the skeleton. */
  product?: MecProduct;
}

const CompareBarCard = ({ product }: CompareBarCardProps) => {
  const locale = useLocale();
  const priceGroup = useProductPrice(product);
  // Only the display price is used; the design shows no sale or member treatment.
  const { priceString } = useFormattedPrice(priceGroup);

  if (!product) {
    return (
      <div className={styles.card} data-testid="compare-bar-card-loading">
        <Skeleton height={THUMBNAIL_SIZE} />
      </div>
    );
  }

  // Prefer the `_transparent` cut of the primary shot, the same way
  // ProductCompareTable's ProductColumn and the PDP gallery do: the opaque cut
  // carries a baked-in white box that reads as a hard square against the card's
  // tinted image tile. Products whose image alt text is not pipe-delimited have
  // no PRI image at all, so they fall back to the first image rather than
  // resolving nothing.
  const priImages = (product.images ?? []).filter(isPrimaryProductImage);
  const image =
    normalizeImageVariantPreference(priImages, {
      preferTransparent: true,
    })[0] ?? product.images?.[0];

  // `urlOriginal` is the untouched upload, and the transparent cuts are the
  // heavy ones — multi-megabyte PNGs that blow `next/image`'s 7s upstream fetch
  // timeout and leave the tile broken. At 60px the standard CDN derivative is
  // more than enough (see selectPrimaryProductImageUrl for the same tradeoff).
  const imageSrc = image?.url || image?.urlOriginal;

  const href = `/${locale}/product/${product.code}/${encodeURIComponent(
    product.slug.toLowerCase()
  )}`;

  return (
    <div className={styles.card}>
      <a
        className={styles.link}
        href={href}
        aria-label={
          priceString ? `${product.name}, ${priceString}` : product.name
        }
      >
        <span className={styles.imageWrapper}>
          {imageSrc && (
            <Image
              src={imageSrc}
              alt=""
              width={THUMBNAIL_SIZE}
              height={THUMBNAIL_SIZE}
              sizes={`${THUMBNAIL_SIZE}px`}
              className={styles.image}
            />
          )}
        </span>
        <span className={styles.details}>
          <span className={styles.name}>{product.name}</span>
          {priceString && <span className={styles.price}>{priceString}</span>}
        </span>
      </a>
    </div>
  );
};

export default CompareBarCard;
